import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import { DataAccessScreensizeFacade } from '@planone/bau365/data-access-screensize';
import { ContactPerson, Contributor, Event as EventInterface, Exhibitor } from '@planone/bau365/util-fair-data';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { BreadcrumbElement, ModalBottomsheetHelper, ModalBottomsheetType } from '@planone/shared/ui-elements';
import { LinkClickEvent } from '@planone/shared/util-interfaces';
import { localizedMeta } from '@planone/shared/util-meta';
import { ImageFileTypes, ImageUrlPipe } from '@planone/shared/util-pipelines';
import { map, Observable, Subject } from 'rxjs';

@Component({
  selector: 'bau365-contact-person-details',
  templateUrl: './contact-person-details.component.html',
  styleUrls: ['./contact-person-details.component.scss'],
})
export class ContactPersonDetailsComponent implements OnChanges, OnDestroy {
  @Input()
  exhibitor: Exhibitor | undefined | null;

  @Input()
  event: EventInterface | undefined | null;

  @Input()
  contactPerson: ContactPerson | Contributor | undefined | null;

  @Input()
  pageTitle: string | undefined;

  @Input()
  pageContext: string | undefined;

  @Input()
  socialShareUrl: string | undefined;

  @Input()
  breadcrumbElements: BreadcrumbElement[] | undefined;

  @Output()
  trackClickEvent: EventEmitter<LinkClickEvent> = new EventEmitter<LinkClickEvent>();

  @Output()
  trackShareDialogOpened: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  trackShareButtonClicked: EventEmitter<string> = new EventEmitter<string>();

  size$: Observable<string | undefined>;
  hallPlanToggle$: Observable<boolean | undefined>;
  hallPlanActive$: Observable<boolean | undefined>;

  initials = '';
  name = '';
  contactInfoPresent = false;
  socialLinksPresent = false;
  size: string | undefined;
  emailSubject = localizedMeta['shareEmailSubject'];
  emailBody = localizedMeta['shareEmailBody'];
  ImageFileTypes = ImageFileTypes;
  imageUrlPipeline: ImageUrlPipe;

  private unsubscribe: Subject<void> = new Subject<void>();

  @ViewChild('tabs') tabs?: unknown;
  @ViewChild('brands') brandsSection?: ElementRef;
  @ViewChild('shareButtonsTitle') public shareButtonsTitle: TemplateRef<never> | null | undefined;
  @ViewChild('shareButtons') public shareButtons: TemplateRef<never> | null | undefined;

  constructor(
    private ScreensizeFacade: DataAccessScreensizeFacade,
    private modalBottomsheetHelper: ModalBottomsheetHelper,
    private sanitizer: DomSanitizer,
    public togglesFacade: DataAccessTogglesFacade,
    private fairsFacade: DataAccessFairsFacade
  ) {
    this.size$ = ScreensizeFacade.size$;
    this.imageUrlPipeline = new ImageUrlPipe(this.sanitizer);
    this.hallPlanToggle$ = togglesFacade.flag('kill-hall-plan').pipe(map((hallplanDisabled) => !hallplanDisabled));
    this.hallPlanActive$ = fairsFacade.fairConfig$.pipe(map((fair) => !!fair?.hallplan));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['contactPerson']) {
      const first = this.contactPerson?.first_name ?? '';
      const second = this.contactPerson?.name ?? '';

      this.initials = first.charAt(0).toUpperCase() + second.charAt(0).toUpperCase();
      this.name = (this.contactPerson?.title ? this.contactPerson?.title + ' ' : '') + first + ' ' + second;
      this.contactInfoPresent = !!(
        this.contactPerson?.phone ||
        this.contactPerson?.mobile ||
        this.contactPerson?.fax ||
        this.contactPerson?.website
      );

      this.socialLinksPresent = !!(
        this.contactPerson?.social_media && Object.keys(this.contactPerson?.social_media).length
      );

      if (this.pageContext) {
        this.emailBody += $localize`:@@pageContactPerson-emailShare-entityType-contactPerson:Ansprechpartner ${this.name}`;
      }
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  get avatarStyle() {
    if (!this.contactPerson?.avatar?.resource) {
      return null;
    }

    return `background-image:url("${this.imageUrlPipeline.transform(
      this.contactPerson.avatar.resource,
      this.contactPerson.avatar.provider,
      {
        width: 200,
        height: 200,
        fileType: ImageFileTypes.WEBP,
      }
    )}");`;
  }

  trackClick(element: EventTarget | null, linkType: string, linkText?: string, contactPersonId?: string) {
    if (this.exhibitor && this.pageContext) {
      this.trackClickEvent.emit({
        htmlElement: element,
        linkType: linkType,
        exhibitor: this.exhibitor,
        event: this.event ?? undefined,
        context: this.exhibitor.fair_id,
        pageType: this.pageContext,
        pageTitle: this.pageTitle,
        ...(linkText && { linkText: linkText }),
        ...(contactPersonId && { contactPersonId: contactPersonId }),
      });
    }
  }

  openShareButtonDialog(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    this.trackShareDialogOpened.emit();

    this.modalBottomsheetHelper.openDialog(
      ModalBottomsheetType.share,
      this.shareButtonsTitle,
      this.shareButtons,
      this.size
    );
  }

  shareButtonClick(network: string) {
    if (this.contactPerson && this.exhibitor && this.pageContext) {
      this.trackShareButtonClicked.emit(network);
    }
  }

  isContributor(item: ContactPerson | Contributor) {
    return 'contributor_role' in item;
  }
}
